import {
  DueDiligencePageGetDueDiligenceRecordQuery,
  SettingsPageGetDueDiligenceFunnelQuery,
  StrategyDetailsPageGetDocumentRecordQuery
} from '@/__generated__/graphql';
import { AIAnswerType } from '@/components/dueDiligence-page/dueDiligenceRecord/types';
import { IFieldEditor, IFieldEditorAttachment } from '@/shared/types/fieldEditor';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { AIFieldType } from '@trace-8/trace8-schema';

export const getFieldType = (field: IFieldEditor): keyof typeof AIFieldType => {
  return field.documentFieldType;
};

export const hasAiAnswer = (field: IFieldEditor): boolean => {
  return field.AI?.answers?.length > 0;
};

export const isValueNull = (field: IFieldEditor): boolean => {
  return field.value === null;
};

export const isValueEmpty = (field: IFieldEditor): boolean => {
  return field.value === '';
};

export const isCommentaryNull = (field: IFieldEditor): boolean => {
  return field.commentary === null;
};

export const isCommentaryEmpty = (field: IFieldEditor): boolean => {
  return field.commentary === '';
};

export const shouldShowAcceptButton = (field: IFieldEditor): boolean => {
  if (!hasAiAnswer(field)) return false;

  const fieldType = getFieldType(field);
  if (
    fieldType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT) ||
    fieldType === getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT)
  ) {
    return isValueNull(field);
  }
  return isValueNull(field) && isCommentaryNull(field);
};

export const shouldShowClearButton = (field: IFieldEditor): boolean => {
  return (hasAiAnswer(field) && field.value !== '' && field.commentary !== '') || (field.value !== null && field.commentary !== null);
};

export const shouldDisplayField = (field: IFieldEditor) => {
  return (hasAiAnswer(field) && isValueNull(field) && isCommentaryNull(field)) || !!field.value || !!field.commentary;
};

export const getAiAnswer = (field: IFieldEditor): string => {
  return field.AI?.answers?.[0] || '';
};

export const getAiCitationsCount = (field: IFieldEditor): number => {
  return field.AI?.citations?.length || 0;
};

export const hasLinkedField = (field: IFieldEditor): boolean => {
  return !!field.linkedField;
};

export const isLinkedFieldActive = (field: IFieldEditor): boolean => {
  return field?.linked === true;
};

export const shouldShowOverrideButton = (field: IFieldEditor): boolean => {
  return hasLinkedField(field) && isLinkedFieldActive(field);
};

export const shouldShowRelinkButton = (field: IFieldEditor): boolean => {
  return hasLinkedField(field) && !isLinkedFieldActive(field);
};

export const shouldShowLinkedFieldActions = (field: IFieldEditor): boolean => {
  return hasLinkedField(field) && (shouldShowOverrideButton(field) || shouldShowRelinkButton(field));
};

export const shouldShowStandardActions = (field: IFieldEditor): boolean => {
  return !hasLinkedField(field) || !isLinkedFieldActive(field);
};

export const convertDueDiligenceSettingsFieldToFieldEditor = (
  fields: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number]['fields']
): IFieldEditor[] => {
  return fields.map((field) => ({
    id: field.id,
    fieldId: field.id,
    fieldAnswerId: null as string | null,
    recordId: null as string | null,
    name: field.name,
    documentFieldType: field.type,
    value: null as string | null,
    commentary: null as string | null,
    linkedField: field.linkedField,
    required: field.required,
    attachments: [] as IFieldEditorAttachment[],
    AI: null as AIAnswerType | null,
    promptGroup: field.promptGroup
      ? {
          id: field.promptGroup?.id,
          prompts: field.promptGroup?.prompts,
          summaryPrompt: field.promptGroup?.summaryPrompt,
          contextPrompt: field.promptGroup?.contextPrompt,
          outputStructure: field.promptGroup?.outputStructure,
          useCustomContextPrompt: field.promptGroup?.useCustomContextPrompt,
          useCustomSummaryPrompt: field.promptGroup?.useCustomSummaryPrompt
        }
      : field.promptGroup
  })) as IFieldEditor[];
};

export const convertDueDiligenceFieldToFieldEditor = (
  fields: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['fields'],
  recordId: string
): IFieldEditor[] => {
  return fields.map((field) => ({
    id: field.id,
    fieldId: field.id,
    fieldAnswerId: field.data.id,
    recordId,
    name: field.name,
    documentFieldType: field.type,
    value: field.data.value,
    commentary: field.data.commentary,
    linkedField: field.linkedField,
    linked: field.data.linked,
    required: field.required,
    docxKey: field.docxKey,
    attachments:
      field?.data?.attachments?.map((attachment) => ({
        id: attachment.id,
        url: attachment.asset.url,
        assetKey: attachment.asset.assetKey
      })) || [],
    AI: field.AI,
    promptGroup: field.promptGroup
      ? {
          id: field.promptGroup?.id,
          prompts: field.promptGroup?.prompts,
          summaryPrompt: field.promptGroup?.summaryPrompt,
          contextPrompt: field.promptGroup?.contextPrompt,
          outputStructure: field.promptGroup?.outputStructure,
          useCustomContextPrompt: field.promptGroup?.useCustomContextPrompt,
          useCustomSummaryPrompt: field.promptGroup?.useCustomSummaryPrompt
        }
      : field.promptGroup
  })) as IFieldEditor[];
};

export const convertDocumentRecordFieldToFieldEditor = (
  fields: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields']
): IFieldEditor[] => {
  return fields.map((field) => ({
    id: field.id,
    fieldId: field.fieldId,
    fieldAnswerId: field.documentField.id,
    recordId: field.recordId,
    name: field.documentField.name,
    documentFieldType: field.documentField.type,
    value: field.value,
    commentary: field.commentary,
    attachments: convertDocumentRecordFieldAttachmentToFieldEditorAttachment(field?.attachments),
    AI: field.AI,
    promptGroup: field.promptGroup
      ? {
          id: field.promptGroup?.id,
          prompts: field.promptGroup?.prompts,
          summaryPrompt: field.promptGroup?.summaryPrompt,
          contextPrompt: field.promptGroup?.contextPrompt,
          outputStructure: field.promptGroup?.outputStructure,
          useCustomContextPrompt: field.promptGroup?.useCustomContextPrompt,
          useCustomSummaryPrompt: field.promptGroup?.useCustomSummaryPrompt
        }
      : field.promptGroup
  }));
};

export const convertDocumentRecordFieldAttachmentToFieldEditorAttachment = (
  attachments: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number]['attachments']
): IFieldEditorAttachment[] => {
  return (
    attachments?.map((attachment) => ({
      id: attachment.id,
      url: attachment.secure_blob_storage_asset.url,
      assetKey: attachment.secure_blob_storage_asset.assetKey
    })) || []
  );
};
